<script>
import {
    format,
} from '@shared/utils/dates';

export default {
    data() {
        return {
            filterRequestConfig: {},
            storageKey: '',
            defaultFilters: [],
            currentFilters: [],
        };
    },
    methods: {
        buildRequestString(...filters) {
            for (const {
                type,
                data,
                param,
            } of filters) {
                const dataObj = Array.isArray(data)
                    ? {
                        param,
                        data,
                    } : {
                        param,
                        data: [data],
                    };

                this.filtersRequestConfig = Object.fromEntries(Object.entries({
                    ...this.filtersRequestConfig,
                    ...this.getRequestConfigByType(type, dataObj),
                }).filter(([, value]) => value !== ''));

                this.currentFilters = [
                    ...this.currentFilters.filter((filter) => filter.param !== param),
                    {
                        ...dataObj,
                        type,
                    },
                ];
            }

            localStorage.setItem(this.storageKey, JSON.stringify(this.currentFilters));
            this.$emit('filtersRequest', this.filtersRequestConfig);
        },

        initFilters(filters = [], storageKey = '') {
            this.storageKey = storageKey;
            this.defaultFilters = filters;
            this.buildFilters(filters);
        },

        // Array consisting of param, type and values
        buildFilters(filters = []) {
            const savedFilters = JSON.parse(localStorage.getItem(this.storageKey));

            this.currentFilters = filters.map(({
                param,
                type,
                data,
                customItems = [],
            }) => {
                const savedData = this.getFilterByParam(savedFilters, param)?.data ?? [];

                return {
                    param,
                    type,
                    // When resetting handle setting only the required/forced values such as toggles
                    data: savedData.length ? savedData : this.getDefaultData(data),
                    ...customItems.length ? {
                        customItems,
                    } : {},
                };
            });

            localStorage.setItem(this.storageKey, JSON.stringify(this.currentFilters));
        },

        resetFilters() {
            localStorage.removeItem(this.storageKey);
            // Need to re-build the filters instead of setting the currentFilters to empty array due to the default values
            this.filtersRequestConfig = {};
            this.buildFilters(this.defaultFilters);
            this.$emit('filtersRequest', {});
        },

        // Processing filters to translate any specified keys and adding an id for simpler comparisons
        processFilters(filters) {
            let index = 0;

            return filters.map(({
                data = null,
                ...rest
            }) => ({
                ...rest,
                ...data.length ? {
                    data: data.map(({
                        key = '',
                        text = '',
                        ...restData
                    }) => ({
                        id: index++,
                        ...restData,
                        ...(text || key) ? {
                            text: key ? this.$t(key) : text,
                        } : {},
                    })),
                } : {},
            }));
        },

        getRequestConfigByType(type, data) {
            const types = {
                date: this.getDateRequest,
                select: this.getSelectRequest,
                rangeSelect: this.getRangeSelectRequest,
                multiSelect: this.getMultiSelectRequest,
            };

            return types[type]?.(data) ?? {};
        },

        getDateRequest({
            param,
            data = [],
        }) {
            const {
                startDate = '',
                endDate = '',
            } = data[0] ?? {};

            // Consider formatting in the date-range-picker directly. Leaving it here for now due to time constraints
            return {
                [`${param}[from]`]: startDate ? format(new Date(startDate), "yyyy-MM-dd'T'00:00:00'") : '', // eslint-disable-line
                [`${param}[to]`]: endDate ? format(new Date(endDate), "yyyy-MM-dd'T'23:59:59'") : '', // eslint-disable-line
            };
        },

        getSelectRequest({
            param,
            data = [],
        }) {
            const {
                value = '',
            } = data[0] ?? {};

            return {
                [param]: value,
            };
        },

        getRangeSelectRequest({
            param,
            data = [],
        }) {
            const {
                value: {
                    min = '',
                    max = '',
                } = {},
            } = data[0] ?? {};

            return {
                [`${param}[from]`]: min,
                [`${param}[to]`]: max,
            };
        },

        getMultiSelectRequest({
            param,
            data = [],
        }) {
            return {
                [param]: data.map(({ value }) => value),
            };
        },

        getFilter(param) {
            return this.defaultFilters.find((filter) => filter.param === param);
        },

        getFilterByParam(filters, param) {
            return filters?.find((filter) => filter.param === param);
        },

        getSelectedData(param) {
            return this.getFilterByParam(this.currentFilters, param)?.data;
        },

        getDefaultData(data) {
            // Getting any objects marked as default values to build the initial filters
            return data?.filter(({ defaultValue = false }) => defaultValue) ?? [];
        },

    },
};
</script>